import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import BaseCard from "../../components/ui/BaseCard";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseButton from "../../components/ui/BaseButton";
import BaseDropdownV2 from "../../components/ui/BaseDropdownV2";

import { fetchCategories } from "../../services/api/category";

import AddEditCategoryModal from "./modals/AddEditCategoryModal";
import { debounce } from "../../utils/helpers";

const menuOptions = [];

export default function CategoryTable() {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const baseQueryParams = {
    limit: 20,
    page: 1,
    search: searchQuery || undefined,
  };
  const [queryParams, setQueryParams] = useState(baseQueryParams);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);
  const [categoriesList, setCategoriesList] = useState([]);

  const normalize = (payload) => {
    const d = payload?.data ?? payload;
    if (Array.isArray(d)) return { list: d, total: d.length };
    if (Array.isArray(d?.data))
      return { list: d.data, total: d.total ?? d.totalCount ?? d.data.length };
    if (Array.isArray(payload)) return { list: payload, total: payload.length };
    return { list: [], total: 0 };
  };

  const [isCategoryModalOpen, setCategoryModalOpen] = useState(false);
  const [catMode, setCatMode] = useState("add");
  const [catInitial, setCatInitial] = useState(null);

  const {
    isLoading: isCategoriesLoading,
    error: categoriesError,
    data: categoriesData,
    refetch,
  } = useQuery({
    queryKey: ["categories", queryParams],
    queryFn: () => fetchCategories(queryParams),
  });

  useEffect(() => {
    const { list, total } = normalize(categoriesData);
    setCategoriesList(list);
    setRowCount(total);
  }, [categoriesData]);

  useEffect(() => {
    setQueryParams((p) => ({
      ...p,
      page: 1,
      search: searchQuery || undefined,
    }));
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, [searchQuery]);

  const openAdd = () => {
    setCatMode("add");
    setCatInitial(null);
    setCategoryModalOpen(true);
  };

  const openEdit = (row) => {
    setCatMode("edit");
    setCatInitial({ id: row.id, name: row.categoryName });
    setCategoryModalOpen(true);
  };

  const handleMenuClick = (item, row) => {
    if (item.value === "EDIT") openEdit(row);
    if (item.value === "DELETE") {
    }
  };
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
    setQueryParams((p) => ({
      ...p,
      page: newModel.page + 1,
      limit: newModel.pageSize,
    }));
  };

  const debouncedSetSearchQuery = useRef(
    debounce((val) => {
      setSearchQuery(val);
      setPaginationModel((m) => ({ ...m, page: 0 }));
    }, 400)
  );

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    debouncedSetSearchQuery.current(val);
  };

  const columns = [
    {
      field: "category",
      headerName: "Technology Stack",
      flex: 1,
      renderCell: (params) => (
        <button className="text-primary ">{params.value}</button>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <BaseDropdownV2
          menuOptions={menuOptions}
          triggerElement={
            <i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base" />
          }
          onItemClick={(item) => handleMenuClick(item, params.row)}
        />
      ),
    },
  ];

  return (
    <>
      <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
        <div className="flex justify-between items-center w-full gap-4">
          <div className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary"><i className="fa-light fa-filter text-white text-sm"></i></span>
            <p className="text-primary ml-2">Add Filter</p>
          </div>

          <div className="w-[76%]">
            <BaseTextField
              placeholder="Search technology stacks"
              value={searchInput}
              onChange={handleSearch}
              startAdornment={
                <i className="fa-light fa-magnifying-glass text-lg" />
              }
            />
          </div>

          <BaseButton
            className="md:w-auto w-full"
            variant="contained"
            color="primary"
            style={{ padding: "6px 12px" }}
            onClick={openAdd}
          >
            Create New Technology Stack
          </BaseButton>
        </div>
      </div>

      <BaseCard
        className="rounded-tl-xl rounded-tr-xl shadow-lg mt-5"
        elevation={0}
      >
            <BaseDataTableV2
              className="max-h-[calc(100vh-320px)] overflow-auto"
              columns={columns}
              data={categoriesList}
              checkboxSelection
              isLoading={isCategoriesLoading}
              hideFooter={false}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              pageSizeOptions={[10, 20, 50, 100]}
              rowCount={rowCount}
            />
      </BaseCard>

      <AddEditCategoryModal
        open={isCategoryModalOpen}
        mode={catMode}
        initialData={catInitial}
        close={() => setCategoryModalOpen(false)}
        refetchCategories={refetch}
      />
    </>
  );
}
